import { useState } from "react";
import Select from "react-select";
import {
  useAppServices,
  useUploadImage,
} from "../../../../../../hook/services";
import { toast } from "react-toastify";

// --- Constants ---
const featureOptions = [
  { value: "Actions", label: "Actions" },
  { value: "Triggers", label: "Triggers" },
  { value: "Conversation Providers", label: "Conversation Providers" },
];

const appTypeOptions = [
  { value: "Agency", label: "Agency" },
  { value: "Sub Account", label: "Sub Account" },
];

const appInstallOptions = [
  { value: "Agency", label: "Agency" },
  { value: "Anyone", label: "Anyone" },
];

const initialFormData = {
  appId: "",
  appName: "",
  shortDescription: "",
  conversationProviderId: "",
  clientId: "",
  clientSecret: "",
  sso: "",
  funKey: "",
  featuresInclude: [],
  appType: "",
  whoCanInstall: "",
  appImage: null,
};

const AddAppsModal = ({ handleRefresh }) => {
  const AppService = useAppServices();
  const uploadImage = useUploadImage();

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  // --- Handlers ---
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setLoading(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file?.type.startsWith("image/")) {
      toast.error("Please upload a valid image file");
      return;
    }
    setFormData((prev) => ({ ...prev, appImage: file }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFeaturesChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      featuresInclude: selectedOptions?.map((opt) => opt.value) || [],
    }));
  };

  const handleSelectChange = (selectedOption, field) => {
    setFormData((prev) => ({ ...prev, [field]: selectedOption?.value || "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.appName || !formData.appId) {
      toast.error("App ID and App Name are required");
      return;
    }

    try {
      setLoading(true);

      let appImage = null;
      if (formData.appImage) {
        const imageResponse = await uploadImage({
          file: formData.appImage,
          desiredPath: `app/logo/image`,
        });
        if (imageResponse?.response?.data) {
          appImage = imageResponse.response.data;
        }
      }

      const payload = { ...formData, appImage };

      const { response, error } = await AppService.apps.create({ payload });

      if (response) {
        toast.success("App created successfully");
        handleClose();
        handleRefresh?.();
      } else {
        throw new Error(error?.message || "Failed to create app");
      }
    } catch (err) {
      console.error("App creation failed:", err);
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // --- JSX ---
  return (
    <>
      <button
        className="btn btn-primary px-4 py-2 rounded-3 shadow-sm"
        onClick={handleOpen}
      >
        <i className="bi bi-plus-circle me-2"></i>Add New
      </button>

      {open && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-light">
                <h5 className="modal-title fw-bold">Add New App</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                ></button>
              </div>

              <div className="modal-body p-4">
                <form onSubmit={handleSubmit}>
                  {/* Image Upload */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">App Image</label>
                    <div
                      className="border rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light position-relative"
                      style={{
                        cursor: "pointer",
                        height: "200px",
                        textAlign: "center",
                      }}
                      onClick={() =>
                        !formData.appImage &&
                        document.getElementById("imageUploadInput").click()
                      }
                    >
                      {formData.appImage ? (
                        <>
                          <img
                            src={URL.createObjectURL(formData.appImage)}
                            alt="Preview"
                            className="img-fluid rounded"
                            style={{
                              maxHeight: "180px",
                              objectFit: "contain",
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormData((prev) => ({
                                ...prev,
                                appImage: null,
                              }));
                            }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-cloud-upload display-4 text-secondary mb-2"></i>
                          <p className="text-muted mb-0">
                            Click or drag an image to upload
                          </p>
                        </>
                      )}
                      <input
                        type="file"
                        id="imageUploadInput"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{ display: "none" }}
                      />
                    </div>
                  </div>

                  {/* Text Fields */}
                  <div className="mb-3">
                    <label className="form-label">Short Description</label>
                    <input
                      type="text"
                      className="form-control"
                      name="shortDescription"
                      value={formData.shortDescription}
                      onChange={handleChange}
                      placeholder="Enter short summary"
                      required
                    />
                  </div>

                  {[
                    ["App ID", "appId"],
                    ["App Name", "appName"],
                    ["Conversation Provider ID", "conversationProviderId"],
                    ["Client ID", "clientId"],
                    ["Client Secret", "clientSecret"],
                    ["SSO", "sso"],
                  ].map(([label, name]) => (
                    <div className="mb-3" key={name}>
                      <label className="form-label">{label}</label>
                      <input
                        type="text"
                        className="form-control"
                        name={name}
                        value={formData[name]}
                        onChange={handleChange}
                        required={
                          name !== "sso" && name !== "conversationProviderId"
                        }
                      />
                    </div>
                  ))}

                  {/* Select Fields */}
                  <div className="mb-3">
                    <label className="form-label">App Type</label>
                    <Select
                      options={appTypeOptions}
                      onChange={(e) => handleSelectChange(e, "appType")}
                      value={appTypeOptions.find(
                        (opt) => opt.value === formData.appType
                      )}
                      placeholder="Select App Type"
                      isClearable
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Who can install app</label>
                    <Select
                      options={appInstallOptions}
                      onChange={(e) => handleSelectChange(e, "whoCanInstall")}
                      value={appInstallOptions.find(
                        (opt) => opt.value === formData.whoCanInstall
                      )}
                      placeholder="Select Who can install app"
                      isClearable
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Features Include</label>
                    <Select
                      isMulti
                      options={featureOptions}
                      value={featureOptions.filter((opt) =>
                        formData.featuresInclude.includes(opt.value)
                      )}
                      onChange={handleFeaturesChange}
                      placeholder="Select Features"
                    />
                  </div>

                  {/* Submit */}
                  <div className="d-flex justify-content-end mt-4">
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddAppsModal;
